/**
 * Chat API service for communicating with the backend
 */

import axios from 'axios';
import { getFirebaseToken } from '../auth/CognitoAuth';
import { APP_CONFIG } from '../config';

// Support runtime configuration (window.ENV via config.js) or build-time env vars (local/dev)
const getApiUrl = () => {
  // Chat API doesn't use /api/v1 prefix, so strip it if present
  const envUrl = APP_CONFIG.apiUrl || 'http://localhost:8000';
  const baseUrl = envUrl.replace(/\/api\/v1$/, ''); // Remove /api/v1 suffix for chat endpoints

  // Debug: Log the URL being used (helps with localhost testing)
  console.log('[ChatAPI] Using base URL:', baseUrl);

  return baseUrl;
};

export const API_BASE_URL = getApiUrl();

const api = axios.create({
  baseURL: API_BASE_URL,
});

export default api;

// Create axios instance for chat API
const chatApiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 1800000, // 30 minutes timeout for long-running analysis
});

// Attach Firebase token to every request
chatApiClient.interceptors.request.use(async (config) => {
  const token = await getFirebaseToken();
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
}, (error) => Promise.reject(error));
export interface ChatRequest {
  message: string;
  conversation_id?: string | null;
  session_data?: Record<string, any>;
  async_mode?: boolean; // If true, returns job_id immediately
}

export interface ChatResponse {
  conversation_id: string;
  agent_name: string;
  message: string;
  data?: any;
  visualization?: any;
  suggested_actions: string[];
  session_data?: Record<string, any>;  // Session state for persistence across requests
  confidence: number;
  success: boolean;
  timestamp: string;
  job_id?: string; // Present if async_mode=true
}

export interface JobStatus {
  job_id: string;
  conversation_id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress: number; // 0-100
  progress_message: string;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
  result: ChatResponse | null;
  error: string | null;
}

export interface ConversationHistory {
  conversation_id: string;
  messages: Array<{
    role: string;
    content: string;
    timestamp: string;
  }>;
  session_data: Record<string, any>;
}

export interface AgentCapabilities {
  agents: Record<string, string>;
  features: string[];
}

/**
 * Send a chat message and get a response
 */
export async function sendChatMessage(request: ChatRequest): Promise<ChatResponse> {
  try {
    console.log('=== SENDING REQUEST ===', request);
    const response = await chatApiClient.post('/api/chat/message', request);
    console.log('=== AXIOS RESPONSE ===', response);
    console.log('=== RESPONSE DATA ===', response.data);
    return response.data;
  } catch (error: any) {
    console.error('=== API ERROR ===', error);
    console.error('Error response:', error.response);
    const detail = error.response?.data?.detail || error.message || 'Failed to send message';
    throw new Error(detail);
  }
}

/**
 * Get conversation history
 */
export async function getConversationHistory(conversationId: string): Promise<ConversationHistory> {
  try {
    const response = await chatApiClient.get(`/api/chat/conversation/${conversationId}`);
    return response.data;
  } catch (error: any) {
    throw new Error('Failed to fetch conversation history');
  }
}

/**
 * Delete conversation
 */
export async function deleteConversation(conversationId: string): Promise<void> {
  try {
    await chatApiClient.delete(`/api/chat/conversation/${conversationId}`);
  } catch (error: any) {
    throw new Error('Failed to delete conversation');
  }
}

/**
 * Get agent capabilities
 */
export async function getAgentCapabilities(): Promise<AgentCapabilities> {
  try {
    const response = await chatApiClient.get('/api/chat/capabilities');
    return response.data;
  } catch (error: any) {
    throw new Error('Failed to fetch agent capabilities');
  }
}

/**
 * Reset session data
 */
export async function resetSession(conversationId: string): Promise<void> {
  try {
    await chatApiClient.post(`/api/chat/reset-session/${conversationId}`);
  } catch (error: any) {
    throw new Error('Failed to reset session');
  }
}

/**
 * Get job status
 *
 * Poll this endpoint to check if async job is complete
 */
export async function getJobStatus(jobId: string): Promise<JobStatus> {
  try {
    const response = await chatApiClient.get(`/api/chat/jobs/${jobId}`);
    return response.data;
  } catch (error: any) {
    throw new Error(`Failed to fetch job status: ${error.message}`);
  }
}

/**
 * Poll for job completion
 *
 * Automatically polls job status every 2 seconds until complete
 * Returns the final result when done
 */
export async function pollJobUntilComplete(
  jobId: string,
  onProgress?: (progress: number, message: string) => void,
  pollIntervalMs: number = 2000
): Promise<ChatResponse> {
  return new Promise((resolve, reject) => {
    const pollInterval = setInterval(async () => {
      try {
        const status = await getJobStatus(jobId);

        // Update progress callback
        if (onProgress) {
          onProgress(status.progress, status.progress_message);
        }

        // Check if done
        if (status.status === 'completed') {
          clearInterval(pollInterval);
          if (status.result) {
            resolve(status.result);
          } else {
            reject(new Error('Job completed but no result available'));
          }
        } else if (status.status === 'failed') {
          clearInterval(pollInterval);
          reject(new Error(status.error || 'Job failed'));
        }
        // Continue polling for 'pending' and 'processing' statuses
      } catch (error: any) {
        clearInterval(pollInterval);
        reject(error);
      }
    }, pollIntervalMs);
  });
}

/**
 * Send message with async processing
 *
 * Sends message, gets job_id, then polls until complete
 * This bypasses the 30-second load balancer timeout
 */
export async function sendMessageAsync(
  request: ChatRequest,
  onProgress?: (progress: number, message: string) => void
): Promise<ChatResponse> {
  try {
    // Send message with async_mode=true
    const asyncRequest: ChatRequest = {
      ...request,
      async_mode: true
    };

    console.log('=== SENDING ASYNC REQUEST ===', asyncRequest);
    const initialResponse = await chatApiClient.post('/api/chat/message', asyncRequest);
    console.log('=== ASYNC RESPONSE ===', initialResponse.data);

    const jobId = initialResponse.data.job_id;

    // Check if this is sync mode (no job_id means backend processed synchronously)
    if (!jobId) {
      console.log('=== SYNC MODE: Immediate response received ===');
      // Backend processed synchronously, return the response directly
      return initialResponse.data as ChatResponse;
    }

    console.log(`=== POLLING JOB ${jobId} ===`);

    // Poll for completion
    const result = await pollJobUntilComplete(jobId, onProgress);

    console.log('=== JOB COMPLETED ===', result);
    return result;
  } catch (error: any) {
    console.error('=== ASYNC REQUEST ERROR ===', error);
    throw error;
  }
}

/**
 * Stream chat message with Server-Sent Events (SSE)
 *
 * Provides real-time streaming of text, visualizations, and suggested actions.
 * Calls onChunk for each received chunk.
 */
export interface StreamChunk {
  type?: 'text' | 'visualization' | 'actions';
  text?: string;
  visualization?: any;
  actions?: any[];
  tool_call?: {
    tool: string;
    status: 'executing' | 'completed' | 'failed';
    error?: string;
  };
  done?: boolean;
  conversation_id?: string;
  session_data?: Record<string, any>;
  error?: string;
}

export async function sendMessageStream(
  request: ChatRequest,
  onChunk: (chunk: StreamChunk) => void,
  signal?: AbortSignal
): Promise<void> {
  const url = `${API_BASE_URL}/api/agent/message/stream`;

  try {
    const token = await getFirebaseToken();
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(request),
      signal,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const reader = response.body?.getReader();
    const decoder = new TextDecoder();

    if (!reader) {
      throw new Error('No response body reader available');
    }

    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();

      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // Split by newlines to get complete SSE messages
      const lines = buffer.split('\n\n');
      buffer = lines.pop() || ''; // Keep incomplete message in buffer

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const chunk = JSON.parse(line.slice(6)); // Remove 'data: ' prefix

            // DEBUG: Log received chunks
            if (chunk.text) {
              console.log(`[SSE] Text chunk (${chunk.text.length} chars):`, chunk.text.substring(0, 50));
            }

            onChunk(chunk);

            // If error or done, stop processing
            if (chunk.error || chunk.done) {
              return;
            }
          } catch (e) {
            console.error('Failed to parse SSE chunk:', line, e);
          }
        }
      }
    }
  } catch (error: any) {
    // AbortError is a graceful stop — propagate without error chunk
    if (error?.name === 'AbortError') {
      throw error;
    }
    console.error('Streaming error:', error);
    onChunk({ error: error.message || 'Streaming failed' });
    throw error;
  }
}
